<!-- src/views/Login.vue -->

<template>
  <div class="container login-view coredeck-page">
    <div class="center-align login-title">
      <h5>Sign in</h5>
    </div>

    <div class="login-card-grid">
      <!-- GOOGLE LOGIN CARD -->
      <div class="card login-card">
        <div class="card-content login-card-content">
          <span class="card-title center-align">
            Google Login
          </span>

          <div class="login-card-body google-card-body">
            <button
              class="google-login-btn"
              type="button"
              @click.prevent="handleGoogleLogin"
              :disabled="loading"
            >
              <span class="google-icon" aria-hidden="true">
                <svg viewBox="0 0 48 48" width="24" height="24">
                  <path
                    fill="#EA4335"
                    d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                  />

                  <path
                    fill="#4285F4"
                    d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                  />

                  <path
                    fill="#FBBC05"
                    d="M10.53 28.59A14.45 14.45 0 0 1 9.75 24c0-1.59.28-3.13.78-4.59l-7.98-6.19A23.86 23.86 0 0 0 0 24c0 3.86.92 7.5 2.56 10.78l7.97-6.19z"
                  />

                  <path
                    fill="#34A853"
                    d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                  />
                </svg>
              </span>

              <span>
                {{
                  googleLoading
                    ? "Signing in..."
                    : "Continue with Google"
                }}
              </span>
            </button>

            <button
              class="btn-flat switch-account-btn"
              type="button"
              @click.prevent="handleGoogleSwitchAccount"
              :disabled="loading"
            >
              {{
                switchAccountLoading
                  ? "Switching..."
                  : "Switch account"
              }}
            </button>
          </div>
        </div>
      </div>

      <!-- EMAIL LOGIN CARD -->
      <div class="card login-card">
        <div class="card-content login-card-content">
          <span class="card-title center-align">
            Email Login
          </span>

          <div class="login-card-body">
            <div class="input-field">
              <input
                id="email"
                type="email"
                v-model.trim="email"
                autocomplete="email"
                :disabled="loading"
              />

              <label for="email">
                Email
              </label>
            </div>

            <div class="input-field">
              <input
                id="password"
                type="password"
                v-model="password"
                autocomplete="current-password"
                :disabled="loading"
                @keyup.enter="handleEmailLogin"
              />

              <label for="password">
                Password
              </label>
            </div>

            <button
              class="email-outline-login-btn"
              type="button"
              @click.prevent="handleEmailLogin"
              :disabled="loading || !email || !password"
            >
              <span class="email-outline-icon" aria-hidden="true">
                <i class="material-icons">
                  email
                </i>
              </span>

              <span>
                {{
                  emailLoading
                    ? "Signing in..."
                    : "Continue with Email"
                }}
              </span>
            </button>

            <button
              class="btn-flat forgot-password-btn"
              type="button"
              @click.prevent="handleResetPassword"
              :disabled="loading || !email"
            >
              Forgot password?
            </button>

            <div
              v-if="resetInfo"
              class="green-text reset-info"
            >
              {{ resetInfo }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <p
      v-if="error"
      class="red-text center-align login-error"
    >
      {{ error }}
    </p>
  </div>
</template>

<script setup>
import {
  computed,
  nextTick,
  ref,
} from "vue";

import {
  useRouter,
} from "vue-router";

import {
  loginWithGoogle,
  loginWithGoogleSelectAccount,
} from "@/scripts/googleLogin";

import {
  loginWithEmail,
  resetPassword,
} from "@/scripts/emailLogin";

import {
  assertUserIsAllowed,
} from "@/scripts/authGate";

/* global M */

const router =
  useRouter();

const activeLogin =
  ref("");

const error =
  ref("");

const resetInfo =
  ref("");

const email =
  ref("");

const password =
  ref("");

const loading =
  computed(
    () =>
      Boolean(
        activeLogin.value
      )
  );

const googleLoading =
  computed(
    () =>
      activeLogin.value ===
      "google"
  );

const switchAccountLoading =
  computed(
    () =>
      activeLogin.value ===
      "google-switch"
  );

const emailLoading =
  computed(
    () =>
      activeLogin.value ===
      "email"
  );

async function goDashboard() {
  await router.replace({
    name: "VenueDashboard",
  });
}

/**
 * Finish either Google login flow.
 */
async function completeGoogleLogin(
  loginFunction
) {
  const result =
    await loginFunction();

  if (!result?.user) {
    throw new Error(
      "Google sign-in completed but no Firebase user was returned."
    );
  }

  /*
   * Confirm this authenticated Firebase user is also an
   * authorised CoreDeck user.
   */
  await assertUserIsAllowed(
    result.user
  );

  /*
   * Enter CoreDeck only after:
   *
   * 1. Google authentication succeeds
   * 2. Firebase authentication succeeds
   * 3. CoreDeck authorisation succeeds
   */
  await goDashboard();
}

/**
 * NORMAL GOOGLE LOGIN
 *
 * Does not force:
 *
 * - Google account selection
 * - Google consent
 */
async function handleGoogleLogin() {
  if (loading.value) {
    return;
  }

  error.value = "";
  resetInfo.value = "";

  activeLogin.value =
    "google";

  try {
    await completeGoogleLogin(
      loginWithGoogle
    );
  } catch (e) {
    console.error(
      "[LOGIN VIEW GOOGLE ERROR]",
      e
    );

    error.value =
      e?.message ||
      "Google sign-in failed. Please try again.";
  } finally {
    activeLogin.value = "";
  }
}

/**
 * SWITCH GOOGLE ACCOUNT
 *
 * This is the ONLY normal CoreDeck login action that deliberately
 * asks Google to display its account chooser.
 */
async function handleGoogleSwitchAccount() {
  if (loading.value) {
    return;
  }

  error.value = "";
  resetInfo.value = "";

  activeLogin.value =
    "google-switch";

  try {
    await completeGoogleLogin(
      loginWithGoogleSelectAccount
    );
  } catch (e) {
    console.error(
      "[LOGIN VIEW GOOGLE SWITCH ACCOUNT ERROR]",
      e
    );

    error.value =
      e?.message ||
      "Google account switch failed. Please try again.";
  } finally {
    activeLogin.value = "";
  }
}

/**
 * EMAIL/PASSWORD LOGIN
 */
async function handleEmailLogin() {
  if (
    loading.value ||
    !email.value ||
    !password.value
  ) {
    return;
  }

  error.value = "";
  resetInfo.value = "";

  activeLogin.value =
    "email";

  try {
    const result =
      await loginWithEmail(
        email.value,
        password.value
      );

    if (!result?.user) {
      throw new Error(
        "Email sign-in completed but no Firebase user was returned."
      );
    }

    await assertUserIsAllowed(
      result.user
    );

    await goDashboard();
  } catch (e) {
    console.error(
      "[LOGIN VIEW EMAIL ERROR]",
      e
    );

    error.value =
      e?.message ||
      "Email sign-in failed. Please try again.";
  } finally {
    activeLogin.value = "";

    await nextTick();

    if (
      window.M?.updateTextFields
    ) {
      window.M.updateTextFields();
    }
  }
}

/**
 * PASSWORD RESET
 */
async function handleResetPassword() {
  if (
    loading.value ||
    !email.value
  ) {
    return;
  }

  error.value = "";
  resetInfo.value = "";

  activeLogin.value =
    "reset";

  try {
    await resetPassword(
      email.value
    );

    resetInfo.value =
      "Password reset email sent (if the address exists).";
  } catch (e) {
    console.error(
      "[LOGIN VIEW PASSWORD RESET ERROR]",
      e
    );

    error.value =
      e?.message ||
      "Could not send password reset email.";
  } finally {
    activeLogin.value = "";

    await nextTick();

    if (
      window.M?.updateTextFields
    ) {
      window.M.updateTextFields();
    }
  }
}
</script>

<style scoped>
.login-view {
  margin-top: 40px;
  max-width: 980px;
}

.login-title {
  margin-bottom: 18px;
}

.login-title h5 {
  margin: 0;
  font-weight: 500;
}

.login-card-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
  align-items: stretch;
}

.login-card {
  border-radius: 10px;
  overflow: hidden;
  margin: 0;
  min-height: 380px;
}

.login-card-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.login-card .card-title {
  display: block;
  font-size: 20px;
  font-weight: 500;
  margin-bottom: 28px;
}

.login-card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.google-card-body {
  justify-content: center;
  align-items: stretch;
  gap: 16px;
}

.google-login-btn,
.email-outline-login-btn {
  width: 100%;
  min-height: 54px;
  border: 1px solid #dadce0;
  border-radius: 8px;
  background: #eceff1;
  color: #3c4043;
  font-size: 18px;
  font-weight: 500;
  letter-spacing: 0;
  text-transform: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  cursor: pointer;
  box-shadow: none;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.google-login-btn:hover:not(:disabled),
.email-outline-login-btn:hover:not(:disabled) {
  background: #f8fafd;
  border-color: #d2e3fc;
  box-shadow: 0 1px 2px rgba(60, 64, 67, 0.18);
}

.google-login-btn:active:not(:disabled),
.email-outline-login-btn:active:not(:disabled) {
  background: #f1f3f4;
}

.google-login-btn:disabled,
.email-outline-login-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.google-icon,
.email-outline-icon {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.google-icon svg {
  display: block;
}

.email-outline-icon {
  color: #3c4043;
}

.email-outline-icon .material-icons {
  font-size: 24px;
}

.switch-account-btn {
  text-transform: none;
  align-self: center;
}

.forgot-password-btn {
  margin-top: 12px;
  text-transform: none;
  align-self: center;
}

.reset-info {
  margin-top: 12px;
  text-align: center;
}

.login-error {
  margin-top: 18px;
  white-space: pre-wrap;
}

@media only screen and (max-width: 800px) {
  .login-view {
    margin-top: 24px;
    max-width: 560px;
  }

  .login-card-grid {
    grid-template-columns: 1fr;
  }

  .login-card {
    min-height: auto;
  }
}
</style>